<template>
  <div class="contract-details container container_30">
    <div class="contract-details__inner" v-if="isLoaded">
      <md-card-header class="contract-details__header">
        <a class="contract-details__route-back-btn" @click.prevent="emitCloseDetails()">
          <i class="material-icons">keyboard_arrow_left</i>
        </a>
        <div class="md-title">{{ i18n.cont_details() }}</div>
      </md-card-header>
      <md-card-content>
        <detail-row v-if="isRequestContract && isPendingRequest"
                    class="contract-details__status"
                    :prop="i18n.lbl_status()"
                    :value="`${i18n.cont_pending_status()}`"
                    :alignLeft="true" :colWidth="[15]"/>
        <div class="contract-details__counterparty-details">
          <h3>{{i18n.cont_counterparty()}}</h3>
          <detail-row :prop="i18n.com_name()" :value="requestorInfo.kyc.company_name" :alignLeft="true" :colWidth="[15, 20]"/>
          <detail-row :prop="i18n.com_website()" :value="requestorInfo.kyc.website" :alignLeft="true" :colWidth="[15, 20]"/>
          <detail-row :prop="i18n.com_email()" :value="requestorInfo.email" :alignLeft="true" :colWidth="[15, 20]"/>
          <detail-row :prop="i18n.cont_company_id()" :value="requestorInfo.id" :copiable="true" :alignLeft="true" :colWidth="[15]"/>
          <detail-row :prop="i18n.cont_contract_number()" :value="contract.contract_number" :copiable="true" :alignLeft="true" :colWidth="[15]"/>
          <detail-row :prop="i18n.lbl_start_date()" :value="`${i18n.d(contract.start_time)}`" :alignLeft="true" :colWidth="[15, 20]"/>
          <detail-row :prop="i18n.lbl_end_date()" :value="`${i18n.d(contract.end_time)}`" :alignLeft="true" :colWidth="[15, 20]"/>
        </div>
        <div class="contract-details__documents-details">
          <div class="contract-details__documents-details-row">
            <div class="contract-details__document-name">
              <h3>{{i18n.lbl_documents()}}</h3>
            </div>
            <div class="contract-details__document-submitter">
              <h3>{{i18n.lbl_submitted_by()}}</h3>
            </div>
            <div class="contract-details__document-type">
              <h3>{{i18n.lbl_type()}}</h3>
            </div>
            <div class="contract-details__documents-view-btn">
              <!-- open file button -->
            </div>
          </div>
          <div class="contract-details__documents-details-row">
            <div class="contract-details__document-name">
              <i class="contract-details__document-icon material-icons">note</i>
              <p>{{contractFile.name}}</p>
            </div>
            <div class="contract-details__document-submitter">
              <p>{{requestorInfo.kyc.company_name}}</p>
            </div>
            <div class="contract-details__document-type">
              <p>{{contractFile.type}}</p>
            </div>
            <div class="contract-details__documents-view-btn">
              <a :href='getUrl' target='_blank'>{{i18n.lbl_open()}}</a>
            </div>
          </div>
          <detail-row :prop="i18n.lbl_note()" :value="contract.note"
                                              :alignLeft="true"
                                              :colWidth="[15, 85]"/>
        </div>
        <div class="contract-details__reject-details"
             v-if="request.request_state === REQUEST_STATES_STR.permanentlyRejected">
          <h3 class="contract-details__reject-details-title">{{i18n.lbl_reject_reason()}}</h3>
          {{request.reject_reason}}
        </div>
      </md-card-content>
      <md-card-actions class="contract-details__btn-wrapper">
        <md-button class="contract-details__btn md-primary"
                  v-if="isRequestContract && !isRequestor"
                  :disabled="isPending || !isPendingRequest"
                  @click="isRejectingRequest = true">
          {{i18n.lbl_reject()}}
        </md-button>
        <md-button class="contract-details__btn md-primary"
                  v-if="isRequestContract && !isRequestor"
                  :disabled="isPending || !isPendingRequest"
                  @click="signContract">
          {{i18n.cont_sign()}}
        </md-button>
        <md-button class="md-primary"
          v-if="!isRequestContract && !isContractCompleted && request.invoices"
          @click="isStartingDispute = true"
          :disabled="isPending || !!request.dispute || !hasConfirmedInvoices">{{ i18n.cont_start_a_dispute() }}</md-button>
        <md-button class="md-primary"
          v-if="!isRequestContract && request.invoices"
          :disabled="isPending || !isRequestor && isConfirmedByCustomer  || isConfirmedByContractor && isRequestor || !hasConfirmedInvoices"
          @click="confirmCompleted">{{ i18n.cont_confirm_completed() }}</md-button>
      </md-card-actions>
    </div>
    <contracts-invoice v-if="!isRequestContract && isLoaded"
                      :contractId="id"
                      :customerId="customerId"
                      :requestorId="requestorInfo.id"
                      :contractCompleted="isContractCompleted" />
    <contract-dispute v-if="request && request.dispute"
                      :contractId="id"
                      :notes="request.notes"
                      :dispute="request.dispute"
                      :escrow="request.escrow"
                      @load-contract="loadContract"/>
    <modal v-if="isRejectingRequest"
           @close-request="isRejectingRequest = false"
           maxWidth="800px">
      <reject-form @submit-reject-message="rejectContract"
                   @close-request="isRejectingRequest = false"/>
    </modal>
    <modal v-if="isStartingDispute"
           @close-request="isStartingDispute = false"
           maxWidth="1200px">
      <start-dispute :contractId="request.id"
                     @close-request="closeDisputeForm"/>
    </modal>
  </div>
</template>

<script>
import config from '@conf'
import { i18n } from '@js/i18n'
import DetailRow from '@vue/app/common/Detail.Row'
import Modal from '@vue/app/common/Modal'
import ContractsInvoice from './Contracts.Invoice'
import ContractDispute from './Contracts.Dispute'

import StartDispute from './modals/Contracts.StartDispute'
import RejectForm from './modals/Contracts.RejectForm'
import FileField from '@com/fields/FileField'
import FormMixin from '@com/mixins/form.mixin'
import get from 'lodash/get'
import {
  REQUEST_STATES_STR,
  CONTRACTS_DETAILS_TYPES,
  CONTRACT_STATES
} from '@const'

import { usersService } from '@api/users.service'
import { contractsService } from '@api/contracts.service'
import { reviewableRequestsService } from '@api/reviewable_requests.service'
import {mapActions, mapGetters} from 'vuex'
import { vuexTypes } from '@vuex/types'

import { EventDispatcher } from '@js/events/event_dispatcher'
import { ErrorHandler } from '@js/errors/error_handler'
import { accountsService } from '@api/accounts.service'
import {errors} from '@js/errors/factory'

export default {
  mixins: [FormMixin],
  props: ['id', 'type'],
  components: {
    DetailRow,
    FileField,
    RejectForm,
    Modal,
    ContractsInvoice,
    ContractDispute,
    StartDispute
  },
  data: _ => ({
    request: null,
    contract: null,
    requestorInfo: {
      id: '',
      email: '',
      kyc: null
    },
    customerId: '',
    isLoaded: false,
    i18n,
    form: {
      note: ''
    },
    document: {
      contract: null
    },
    isRejectingRequest: false,
    isStartingDispute: false,
    REQUEST_STATES_STR,
    CONTRACTS_DETAILS_TYPES,
    CONTRACT_STATES
  }),

  created () {
    this.loadContract()
  },

  computed: {
    ...mapGetters([
      vuexTypes.accountId,
      vuexTypes.factorData
    ]),
    isRequestContract () {
      return this.type === CONTRACTS_DETAILS_TYPES.requests
    },
    isArchivedContract () {
      return this.type === CONTRACTS_DETAILS_TYPES.archived
    },
    isSignedContract () {
      return this.type === CONTRACTS_DETAILS_TYPES.signed
    },
    isRequestor () {
      if (this.request.contractor) return this.request.contractor === this.accountId
      return this.request.requestor === this.accountId
    },
    isPendingRequest () {
      return this.request.request_state === REQUEST_STATES_STR.pending
    },
    getUrl () {
      const key = get(this.contract, 'contract_file.key')
      if (!key) return ''
      return `${config.FILE_STORAGE}/${key}`
    },
    contractFile () {
      const fileName = get(this.contract, 'contract_file.name', '')
      const [name, type] = fileName.split(/\.(?=[^.]+$)/)
      return {
        name: name,
        type: type
      }
    },
    contractState () {
      if (!Array.isArray(this.request.state)) return []
      return this.request.state.map(state => state.value)
    },
    isConfirmedByContractor () {
      return this.contractState.indexOf(CONTRACT_STATES.contractorConfirmed) !== -1
    },
    isConfirmedByCustomer () {
      return this.contractState.indexOf(CONTRACT_STATES.customerConfirmed) !== -1
    },
    isContractCompleted () {
      return this.isConfirmedByContractor && this.isConfirmedByCustomer
    },
    hasConfirmedInvoices () {
      if (!this.request.invoices) return false
      return !!this.request.invoices.find(item => item.request_state === 'waiting_for_confirmation')
    }
  },

  methods: {
    ...mapActions({
      updateTwoFactorData: vuexTypes.SET_TWO_FACTOR_STATE
    }),
    async loadContract () {
      if (this.type === CONTRACTS_DETAILS_TYPES.requests) {
        this.request = await reviewableRequestsService.loadReviewableRequestById(this.id)
        this.requestorId = this.request.requestor
        this.customerId = this.request.reviewer
        this.contract = {
          start_time: get(this.request, 'details.contract.start_time', '-'),
          end_time: get(this.request, 'details.contract.end_time', '-'),
          contract_file: get(this.request, 'details.contract.details.contract', {}),
          contract_number: get(this.request, 'details.contract.details.contract_number', '-'),
          note: get(this.request, 'details.contract.details.note', '-')
        }
      } else {
        this.request = await contractsService.loadContractById(this.id)
        this.requestorId = this.request.contractor
        this.customerId = this.request.customer
        this.contract = {
          start_time: get(this.request, 'start_time', '-'),
          end_time: get(this.request, 'end_time', '-'),
          contract_file: get(this.request, 'initial_details.contract', {}),
          contract_number: get(this.request, 'initial_details.contract_number', '-'),
          note: get(this.request, 'initial_details.note', '-')
        }
      }
      this.loadRequestorInfo()
    },
    async loadRequestorInfo () {
      const requestorKyc = (await usersService.blobsOf(this.requestorId).getAll())[0]
      this.requestorInfo.id = this.requestorId
      this.requestorInfo.email = await accountsService.loadEmailByAccountId(this.requestorId)
      this.requestorInfo.kyc = requestorKyc
      this.isLoaded = true
    },
    emitCloseDetails () {
      this.$emit('close-details')
    },
    closeDisputeForm () {
      this.isStartingDispute = false
      this.loadContract()
    },
    async signContract () {
      this.disable()

      try {
        await contractsService.signContract(this.request)
        await this.loadContract()
        EventDispatcher.dispatchShowSuccessEvent(i18n.contract_signed())
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    },
    async rejectContract (message) {
      this.disable()

      try {
        this.isRejectingRequest = false
        await contractsService.rejectContract(this.request, message)
        await this.loadContract()
        EventDispatcher.dispatchShowSuccessEvent(i18n.contract_rejected())
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    },
    async confirmCompleted () {
      this.disable()

      try {
        await contractsService.confirmCompleted({contractID: this.request.id})
        await this.loadContract()
        EventDispatcher.dispatchShowSuccessEvent(i18n.contract_completed())
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~scss/mixins";
  @import "~scss/variables";

  .contract-details__counterparty-details {
    margin-bottom: 1rem;
  }

  .contract-details__documents-details-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .contract-details__document-name {
    width: 65%;
    display: flex;
    align-items: center;
  }
  .contract-details__document-submitter {
    width: 20%;
    text-align: center;
  }

  .contract-details__document-type {
    width: 10%;
    text-align: center;
  }

  .contract-details__documents-view-btn {
    text-align: center;
    width: 5%;
  }
  .contract-details__document-icon {/*
    font-size: $material-icon-big-but-not-so-big;*/
    margin-right: .5rem;
  }

  .contract-details__btn-wrapper {
    display: flex;
    margin-top: 1rem;
    justify-content: flex-end;
  }

  .contract-details__header {
    display: flex;
    align-items: center;
    & .contract-details__route-back-btn {
      margin-left: -.5rem;
      margin-right: 1rem;
      font-size: 2.5rem;
    }
  }

  .contract-details__inner {
    background: $col-background-content;
  }

  .contract-details__status { margin-bottom: 2rem; }
  .contract-details__reject-details { margin-top: 1rem; }
</style>



// WEBPACK FOOTER //
// src/vue/app/contracts/index/Contracts.Details.vue